import { FC, useState } from "react";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { useFormContext } from "react-hook-form";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { DairyIntervention } from "../../../../models/Intervention";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowControlledTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import SustellRationFeeds from "../../../../../modules/Farms/Baseline/SustellRationFeeds_v2";
import {
  CompoundFeedDatabase,
  MasterDataOption,
  Maybe,
} from "../../../../../../graphql/types";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { Spacer } from "../../../helpers/Spacer";
import { CattleFeed as CattleBaselineFeed } from "../../../../models/Baseline/CattleBaseline";
import { BaselineFeed } from "../../../../models/Baseline";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";

interface ICompoundFeed {
  display_name: string;
  value: string;
  databaseFoundation: CompoundFeedDatabase;
}

interface IBeefFeedCompoundFeedProp {
  compoundFeeds?: Maybe<Array<MasterDataOption>>;
  databaseFoundation: CompoundFeedDatabase;
  formType: FormType;
  fieldItemPrefix: string;
  baselineStageData: CattleBaselineFeed;
  currentFormValues: CattleFeed | undefined | null;
  isCompoundFeedEdit: boolean;
  setIsCompFeedEdit: (val: boolean) => void;
}

const BeefFeedCompoundFeed: FC<IBeefFeedCompoundFeedProp> = ({
  compoundFeeds,
  databaseFoundation,
  formType = FormType.Add,
  fieldItemPrefix,
  baselineStageData,
  currentFormValues,
  isCompoundFeedEdit,
  setIsCompFeedEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<DairyIntervention>();
  const [childKey, setChildKey] = useState(0);

  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const originalBarnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);

  let barnOutputMassUnit: string;
  switch (originalBarnOutputMassUnit) {
    case "kg":
      barnOutputMassUnit = "kg DM";
      break;
    case "lbs":
      barnOutputMassUnit = "lbs DM";
      break;
    default:
      originalBarnOutputMassUnit || "kg DM";
      break;
  }

  const compoundFeedsAdditionsWatch = fc.watch(
    `${fieldItemPrefix}.compoundFeedsAdditions`
  ) as BaselineFeed[];

  const cancelEditionHandler = () => {
    (baselineStageData?.compoundFeeds || [])
      ?.filter((item) => !!item.feedType)
      ?.forEach((_, index) => {
        const lastValue = currentFormValues?.compoundFeeds?.[index];
        fc.setValue(
          `${fieldItemPrefix}.compoundFeeds[${index}].kgPerAnimal`,
          lastValue?.kgPerAnimal ?? ""
        );
      });

    const feedsAdditionsPrefix = `${fieldItemPrefix}.compoundFeedsAdditions`;
    fc.setValue(feedsAdditionsPrefix, []);

    const feedsAdditions =
      currentFormValues?.compoundFeedsAdditions as BaselineFeed[];
    if (feedsAdditions?.length) {
      feedsAdditions?.forEach((item, index) => {
        Object.keys(item).forEach((key) => {
          const path = `${feedsAdditionsPrefix}[${index}].${key}`;
          fc.setValue(path, item[key as keyof typeof item] ?? "");
        });
      });
    }

    setChildKey((prev) => prev + 1);
    setIsCompFeedEdit(false);
  };

  const getBaselineCompoundFeedLabel = (id: string) => {
    const feedDisplayName = compoundFeeds?.find(
      (item) => item.value === id
    )?.display_name;
    return feedDisplayName || "unknown";
  };

  const getCompoundFeedsList = (databaseOption: CompoundFeedDatabase) => {
    const feeds = (compoundFeeds as unknown as ICompoundFeed[]) || [];
    return feeds.filter(
      (feed: ICompoundFeed) => feed.databaseFoundation === databaseOption
    );
  };

  return (
    <div>
      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.FEED.COMPOUND_FEED" })}
      </p>
      <DsmGrid
        className={classes.dsmGridOneColumnWithBorder}
        style={{
          padding: "var(--dsm-spacing-px-4)",
        }}
      >
        {(baselineStageData?.compoundFeeds || [])
          ?.filter((item) => !!item.feedType)
          ?.map((feedItem, i: number) =>
            !isCompoundFeedEdit &&
              !fc.getValues(
                `${fieldItemPrefix}.compoundFeeds[${i}].kgPerAnimal`
              ) ? (
              <>
                <DsmGrid className={classes.dsmGridTwoColumnsInterventionComponent}
                >
                  <ReactHookDsmInput
                    key={`${fieldItemPrefix}.compoundFeeds[${i}].originalKgPerAnimal`}
                    tooltip={intl.formatMessage({ id: "INTERVENTION.FORM.BARN.RATION.COMPOUND_FEEDS.TOOLTIP" })}
                    defaultValue={feedItem.kgPerAnimal ?? ""}
                    name={`${fieldItemPrefix}.compoundFeeds[${i}].originalKgPerAnimal`}
                    label={getBaselineCompoundFeedLabel(feedItem.feedType)}
                    adornment={barnOutputMassUnit}
                    disabled
                    type="number"
                  />
                </DsmGrid>
                <Spacer />
              </>
            ) : (
              <DsmGrid className={classes.dsmGridTwoColumnsInterventionComponent}>
                <RowControlledTextFieldWithMetrics
                  name={`${fieldItemPrefix}.compoundFeeds[${i}].kgPerAnimal`}
                  label={getBaselineCompoundFeedLabel(feedItem.feedType)}
                  tooltip={intl.formatMessage({
                    id: "INTERVENTION.FORM.BARN.RATION.COMPOUND_FEEDS.TOOLTIP",
                  })}
                  metricUnit={intl.formatMessage(
                    { id: "ADDORNMENT.UNITS.WITH_PER_ANIMAL_SUFIX" },
                    { unit: barnOutputMassUnit }
                  )}
                  baseline={feedItem.kgPerAnimal}
                  disabled={formType === "view" || !isCompoundFeedEdit}
                  minAllowedValue={0}
                  displayFormattedUnit={false}
                  originalMetricUnit={barnOutputMassUnit}
                  displayTypeSelect={isCompoundFeedEdit}
                />
                <Spacer />
              </DsmGrid>
            )
          )}
        {(isCompoundFeedEdit ||
          (!isCompoundFeedEdit &&
            compoundFeedsAdditionsWatch?.length !== 0)) && (
            <>
              <Spacer />
              <SustellRationFeeds
                key={`compoundFeedsAdditions-${childKey}`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.FEED.COMPOUND_FEED",
                })}
                availableFeedItems={getCompoundFeedsList(databaseFoundation)}
                fieldItemPrefix={`${fieldItemPrefix}`}
                subFieldName="compoundFeedsAdditions"
                tooltip={intl.formatMessage({
                  id: "BASELINE.FORM.BARN.RATION.COMPOUND_FEEDS.TOOLTIP",
                })}
                formState={isCompoundFeedEdit ? formType : FormType.View}
                compoundFeed
                feedData={compoundFeedsAdditionsWatch}
                allowNewFeeds={isCompoundFeedEdit}
              />
            </>
          )}
        <Spacer />
        {formType !== FormType.View &&
          <EditModeButtons
            isEditing={isCompoundFeedEdit}
            onEdit={() => setIsCompFeedEdit(true)}
            onCancel={cancelEditionHandler}
          />}
      </DsmGrid>
    </div>
  );
};

BeefFeedCompoundFeed.defaultProps = {
  compoundFeeds: [],
};

export default BeefFeedCompoundFeed;
